<template lang="html">
  <v-container fluid>
    <v-skeleton-loader v-if="loading" type="article, list-item@4" class="mx-auto" max-width="800" />

    <v-row v-else-if="soalList.length" justify="center">
      <v-col cols="12" sm="3" md="2" class="d-flex justify-center justify-sm-end">
        <question-navigator
          :total="soalList.length"
          :current-index="currentIndex"
          :statuses="statuses"
          @navigate="goTo"
        />
      </v-col>

      <v-col cols="12" sm="9" md="10" lg="8">
        <v-card>
          <v-card-title class="d-flex flex-column align-start pb-1">
            <div class="primary--text text-uppercase font-weight-bold">
              {{ currentSoal.kompetensi }}
            </div>
          </v-card-title>

          <v-card-subtitle class="pt-0 mt-2">
            <div class="d-flex justify-space-between align-center mb-1">
              <span>Pertanyaan {{ currentIndex + 1 }} / {{ soalList.length }}</span>
              <span>{{ answeredCount }} / {{ soalList.length }} dijawab</span>
            </div>
            <v-progress-linear
              :value="progress"
              height="8"
              rounded
              color="primary"
            />
          </v-card-subtitle>

          <v-card-text>
            <v-window v-model="currentIndex" touch>
              <v-window-item
                v-for="soal in soalList"
                :key="soal.id"
              >
                <div class="mb-4 text-body-1" style="line-height: 1.6;">
                  {{ soal.soal }}
                </div>
                <v-radio-group
                  v-model="answers[soal.id]"
                  column
                  @change="onAnswerChange(soal.id)"
                >
                  <v-radio
                    v-for="p in soal.pilihan"
                    :key="p.id"
                    :value="p.id"
                    class="mb-3"
                  >
                    <template v-slot:label>
                      <span class="text-body-2" style="white-space: normal; line-height: 1.45;">
                        {{ p.pilihan }}
                      </span>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-window-item>
            </v-window>

            <v-alert
              v-if="hasUnansweredGap && !allAnswered"
              type="info"
              dense
              text
              class="mt-2"
            >
              Ada pertanyaan yang belum dijawab.
              <v-btn text small color="primary" @click="jumpToUnanswered">
                Lompat ke pertanyaan belum terjawab
              </v-btn>
            </v-alert>
          </v-card-text>

          <v-card-actions class="px-4 pb-4 flex-wrap">
            <v-btn
              outlined
              color="primary"
              :disabled="currentIndex === 0"
              @click="prev"
            >
              <v-icon left>mdi-chevron-left</v-icon>
              Sebelumnya
            </v-btn>

            <v-spacer />

            <confirm-btn
              v-if="allAnswered"
              title="Konfirmasi"
              content="Yakin dengan seluruh jawaban Anda? Jawaban akan dikirim dan tidak dapat diubah."
              :btnProps="btnProps"
              @on-agree="onSubmit"
            />

            <v-btn
              v-if="!isLast"
              color="primary"
              class="ml-2"
              :disabled="!hasCurrentAnswer"
              @click="next"
            >
              Berikutnya
              <v-icon right>mdi-chevron-right</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-alert v-else-if="!loading" type="warning" prominent>
      Tidak ada soal kompetensi yang tersedia.
    </v-alert>

    <v-dialog v-model="showRestoreDialog" persistent max-width="450">
      <v-card>
        <v-card-title class="headline">
          Lanjutkan pengisian?
        </v-card-title>
        <v-card-text>
          Ditemukan progres pengisian sebelumnya
          <template v-if="pendingDraftAnsweredCount > 0">
            ({{ pendingDraftAnsweredCount }} dari {{ soalList.length }} pertanyaan sudah dijawab).
          </template>
          <template v-else>.</template>
          Apakah Anda ingin melanjutkan dari progres tersebut?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="discardDraft">
            Tidak
          </v-btn>
          <v-btn color="primary" dark @click="restoreDraft">
            Ya
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="submitting">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>
  </v-container>
</template>

<script>
import store from '@/store'
import { local } from '@/store/local'
import _g from '@/global'
import ConfirmBtn from '@/components/base/ConfirmBtn'
import KuesionerService from '@/services/KuesionerService'
import QuestionNavigator from './components/QuestionNavigator'

export default {
  name: 'QuestionKompetensi',
  components: {
    ConfirmBtn,
    QuestionNavigator
  },
  data() {
    return {
      loading: true,
      submitting: false,
      soalList: [],
      currentIndex: 0,
      answers: {},
      visited: {},
      autoAdvanceTimer: null,
      showRestoreDialog: false,
      pendingDraft: null,
      btnProps: {
        color: 'primary',
        name: 'Kirim Jawaban',
        icon: 'mdi-send',
        class: ''
      }
    }
  },
  computed: {
    currentSoal() {
      return this.soalList[this.currentIndex] || { kompetensi: '', indikator: '', soal: '', pilihan: [] }
    },
    isLast() {
      return this.currentIndex >= this.soalList.length - 1
    },
    hasCurrentAnswer() {
      const soal = this.currentSoal
      return soal.id != null && this.answers[soal.id] != null
    },
    answeredCount() {
      return Object.keys(this.answers).filter(id => this.answers[id] != null).length
    },
    allAnswered() {
      return this.soalList.length > 0 && this.answeredCount === this.soalList.length
    },
    progress() {
      if (!this.soalList.length) return 0
      return (this.answeredCount / this.soalList.length) * 100
    },
    hasUnansweredGap() {
      if (!this.soalList.length) return false
      const firstUnanswered = this.soalList.findIndex(s => this.answers[s.id] == null)
      return firstUnanswered !== -1 && firstUnanswered < this.currentIndex
    },
    statuses() {
      return this.soalList.map(s => {
        if (this.answers[s.id] != null) return 'answered'
        if (this.visited[s.id]) return 'skipped'
        return 'pending'
      })
    },
    storageKey() {
      const nip = _g.getCurrentUsername()
      const year = store.state.user.current.year
      return `kompetensi_draft_${nip}_${year}`
    },
    pendingDraftAnsweredCount() {
      if (!this.pendingDraft || !this.pendingDraft.answers) return 0
      return Object.keys(this.pendingDraft.answers).filter(
        id => this.pendingDraft.answers[id] != null
      ).length
    }
  },
  watch: {
    currentIndex(newIndex, oldIndex) {
      if (oldIndex == null || oldIndex === newIndex) return
      const prevSoal = this.soalList[oldIndex]
      if (prevSoal && this.answers[prevSoal.id] == null) {
        this.$set(this.visited, prevSoal.id, true)
      }
    }
  },
  created() {
    this.fetchSoal()
  },
  beforeDestroy() {
    if (this.autoAdvanceTimer) {
      clearTimeout(this.autoAdvanceTimer)
    }
  },
  methods: {
    saveDraft() {
      const soalIds = this.soalList.map(s => s.id)
      local.setLocal(this.storageKey, {
        answers: { ...this.answers },
        visited: { ...this.visited },
        currentIndex: this.currentIndex,
        soalIds,
        savedAt: Date.now()
      })
    },
    loadDraft() {
      const draft = local.getLocal(this.storageKey)
      if (!draft || typeof draft !== 'object' || Array.isArray(draft)) return null
      if (!draft.answers || !Array.isArray(draft.soalIds)) return null
      return draft
    },
    clearDraft() {
      local.clearItem(this.storageKey)
      this.pendingDraft = null
    },
    isDraftValid(draft) {
      if (!draft || !draft.answers || !Array.isArray(draft.soalIds)) return false
      const currentIds = this.soalList.map(s => s.id)
      if (draft.soalIds.length !== currentIds.length) return false
      const currentSet = new Set(currentIds.map(String))
      if (!draft.soalIds.every(id => currentSet.has(String(id)))) return false
      const answeredCount = Object.keys(draft.answers).filter(
        id => draft.answers[id] != null
      ).length
      return answeredCount > 0
    },
    offerRestoreIfNeeded() {
      const draft = this.loadDraft()
      if (!this.isDraftValid(draft)) {
        if (draft) this.clearDraft()
        return
      }
      this.pendingDraft = draft
      this.showRestoreDialog = true
    },
    restoreDraft() {
      const draft = this.pendingDraft
      this.showRestoreDialog = false
      if (!draft) return

      this.soalList.forEach(s => {
        const key = s.id
        const answer = draft.answers[key] != null
          ? draft.answers[key]
          : draft.answers[String(key)]
        this.$set(this.answers, key, answer != null ? answer : null)
        const visited = draft.visited && (
          draft.visited[key] != null
            ? draft.visited[key]
            : draft.visited[String(key)]
        )
        if (visited) {
          this.$set(this.visited, key, true)
        }
      })

      const maxIndex = Math.max(this.soalList.length - 1, 0)
      const index = Number(draft.currentIndex) || 0
      this.currentIndex = Math.min(Math.max(index, 0), maxIndex)
      this.pendingDraft = null
    },
    discardDraft() {
      this.showRestoreDialog = false
      this.clearDraft()
    },
    fetchSoal() {
      this.loading = true
      KuesionerService.getSoalKompetensi()
        .then(res => {
          const data = (res.data && res.data.data) || []
          this.soalList = data
          this.answers = {}
          this.visited = {}
          data.forEach(s => {
            this.$set(this.answers, s.id, null)
          })
          this.currentIndex = 0
          this.offerRestoreIfNeeded()
        })
        .catch(err => {
          const message = (err.response && err.response.data && err.response.data.message)
            || err.message
            || 'Gagal memuat soal kompetensi'
          store.commit('snackbar/setSnack', { message, color: 'error' })
        })
        .finally(() => {
          this.loading = false
        })
    },
    goTo(index) {
      if (index < 0 || index >= this.soalList.length || index === this.currentIndex) return
      this.currentIndex = index
    },
    prev() {
      if (this.currentIndex > 0) {
        this.currentIndex -= 1
      }
    },
    next() {
      if (!this.hasCurrentAnswer) return
      if (this.currentIndex < this.soalList.length - 1) {
        this.currentIndex += 1
      }
    },
    onAnswerChange(soalId) {
      this.$set(this.visited, soalId, false)
      this.saveDraft()

      if (this.autoAdvanceTimer) {
        clearTimeout(this.autoAdvanceTimer)
      }

      const answeredIndex = this.soalList.findIndex(s => s.id === soalId)
      if (answeredIndex === -1) return
      if (answeredIndex >= this.soalList.length - 1) return

      this.autoAdvanceTimer = setTimeout(() => {
        if (this.currentIndex === answeredIndex && this.answers[soalId] != null) {
          this.currentIndex = answeredIndex + 1
          this.saveDraft()
        }
      }, 250)
    },
    jumpToUnanswered() {
      const idx = this.soalList.findIndex(s => this.answers[s.id] == null)
      if (idx !== -1) {
        this.currentIndex = idx
      }
    },
    onSubmit() {
      if (!this.allAnswered || this.submitting) return

      const hasil = this.soalList.map(s => ({
        soal_id: s.id,
        pilihan_id: this.answers[s.id]
      }))

      this.submitting = true
      KuesionerService.storeHasilKompetensi(hasil)
        .then(() => {
          this.clearDraft()
          store.commit('snackbar/setSnack', {
            message: 'Jawaban berhasil dikirim',
            color: 'success'
          })
          this.$router.push('/review-kompetensi')
        })
        .catch(err => {
          const message = (err.response && err.response.data && err.response.data.message)
            || err.message
            || 'Gagal mengirim jawaban'
          store.commit('snackbar/setSnack', { message, color: 'error' })
        })
        .finally(() => {
          this.submitting = false
        })
    }
  }
}
</script>
